import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Box } from '@fastly/consistently';
import { Text } from '@components/Typography';
import { useEventMode, useEventModeContext, EventModes } from '@hooks/use-event-mode';
import ClockIcon from '../images/clock.svg';

const ClockContainer = styled.div`
  display: flex;
  flex-wrap: nowrap;
`;

const Timer = styled.div`
  display: flex;
  flex-wrap: nowrap;
`;

const TimerGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  margin-left: 18px;
`;

const Digits = styled.span`
  display: block;
  font-variant-numeric: tabular-nums;
  font-size: 24px;
  line-height: 1.1;
  font-weight: 900;

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    font-size: 32px;
  }
`;

const Label = styled(Text)`
  color: var(--color-red);
`;

const TimerLabel = styled(Text)`
  text-transform: uppercase;
  color: #cbcbcb;
  font-weight: 700;
  margin-bottom: 8px;
`;

const timerLabels = {
  [EventModes.Before]: 'Countdown until the next live event',
  [EventModes.PreRoll]: 'Countdown until the next live event',
  [EventModes.Live]: 'Event is now live',
  [EventModes.After]: null,
};

const StyledIcon = styled(ClockIcon as any)`
  width: 48px;
  height: 48px;
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 52px;
    height: 52px;
  }
`;

const initialTimeLeft = {
  d: '00',
  h: '00',
  m: '00',
  s: '00',
};

type TimeLeft = typeof initialTimeLeft;

const calculateTimeLeft = (target: number, now: number = +new Date()): TimeLeft => {
  const difference = target - now;
  let timeLeft = initialTimeLeft;

  if (difference > 0) {
    timeLeft = {
      d: Math.floor(difference / (1000 * 60 * 60 * 24))
        .toString()
        .padStart(2, '0'),
      h: Math.floor((difference / (1000 * 60 * 60)) % 24)
        .toString()
        .padStart(2, '0'),
      m: Math.floor((difference / 1000 / 60) % 60)
        .toString()
        .padStart(2, '0'),
      s: Math.floor((difference / 1000) % 60)
        .toString()
        .padStart(2, '0'),
    };
  }

  return timeLeft;
};

/**
 * EventModes.Before Clock should show time until next event
 * EventModes.Live Clock should show 00:00:00:00
 * EventModes.After Clock should return null
 */
const Clock = (): JSX.Element | null => {
  const [eventModeContext, setEventMode] = useEventModeContext();
  const { mode, allSessions } = eventModeContext;

  const now: Date = new Date();

  const nextEventStart: number = allSessions.nodes.reduce((next, { date }) => {
    if (next) return next;

    if (!date) return 0;

    const sessionStart = new Date(date);
    if (now < sessionStart) return +sessionStart;

    return 0;
  }, 0);

  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft(nextEventStart, +now));

  useEffect(() => {
    // continuously calculate the event mode
    // if the new mode is different, set the event mode state
    const timer = setInterval(() => {
      const newEventMode = useEventMode(allSessions);
      if (newEventMode.mode !== mode) {
        setEventMode(newEventMode);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [mode]);

  useEffect(() => {
    if (mode === EventModes.Before || mode === EventModes.PreRoll) {
      const timer = setTimeout(() => {
        setTimeLeft(calculateTimeLeft(nextEventStart, +new Date()));
      }, 1000);
      return () => clearTimeout(timer);
    }

    if (mode === EventModes.Live) {
      setTimeLeft(calculateTimeLeft(0));
    }
  }, [mode, setTimeLeft, timeLeft, nextEventStart]);

  const { d, h, m, s } = timeLeft;

  if ([EventModes.Before, EventModes.PreRoll, EventModes.Live].includes(mode)) {
    return (
      <Box
        as="section"
        display="flex"
        flexDirection="column"
        alignItems="flex-end"
        justifySelf="center"
        gridRow={['2', '2', 'auto']}
        gridColumn={['1 / span 2', '1 / span 2', 'auto']}
      >
        <TimerLabel size="xs">{timerLabels[mode]}</TimerLabel>
        <ClockContainer>
          <StyledIcon />
          <Timer role="timer">
            <TimerGroup>
              <Digits>{d}</Digits>
              <Label size="md">Days</Label>
            </TimerGroup>
            <TimerGroup>
              <Digits>{h}</Digits>
              <Label size="md">Hours</Label>
            </TimerGroup>
            <TimerGroup>
              <Digits>{m}</Digits>
              <Label size="md">Mins</Label>
            </TimerGroup>
            <TimerGroup>
              <Digits>{s}</Digits>
              <Label size="md">Secs</Label>
            </TimerGroup>
          </Timer>
        </ClockContainer>
      </Box>
    );
  }

  return null;
};

export default Clock;
